import { logger } from '@/utils/logger/logger';
import { type WebinarTimeslotValue } from '@heidi/growth/data';

export interface WebinarSignupParams {
  firstName: string;
  lastName: string;
  email: string;
  timeslot: WebinarTimeslotValue;
}

/**
 * Submits a webinar signup to HubSpot.
 *
 * Errors are caught and logged (never thrown) so legacy fire-and-forget
 * callers using `void submitWebinarSignup({...})` keep working. Returns a
 * success boolean so newer UI callers can branch on the outcome — e.g.
 * the SLG flow needs to show an error and let the user retry instead of
 * advancing to the confirmation screen when HubSpot rejected it.
 */
export async function submitWebinarSignup(params: WebinarSignupParams): Promise<boolean> {
  const { firstName, lastName, email, timeslot } = params;

  try {
    const response = await fetch('/api/hubspot/submit-webinar-signup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        firstname: firstName,
        lastname: lastName,
        email,
        [timeslot.hubspotProperty]: timeslot.value,
      }),
    });

    if (!response.ok) {
      const data = (await response.json()) as { message?: string };
      logger.warn('HubSpot webinar signup failed:', data.message);
      return false;
    }
    return true;
  } catch (error) {
    logger.warn('HubSpot webinar signup error:', error);
    return false;
  }
}
